import {
  AppData,
  AppUnifiedComponentData,
  CompRef,
  DocumentServicesObject,
  AppDataComponent,
} from '@wix/document-services-types';
import {
  BaseInfo,
  ComponentType,
  ContainerData,
  UnifiedWidget,
} from '../../../types/unifiedComponents';
import { APP_SCHEMA_VERSIONS } from './installation/constants';
import { getComponentId } from '../../utils/component';
import { IExperimentsApiAdapter } from '../../utils/experimentsApiAdapter';

export const getPageDataByComponentId = (
  appData?: AppData,
  componentId?: string,
): ContainerData | undefined => {
  if (!componentId || !appData?.components) {
    return;
  }

  const pageComponent = appData.components.find(
    (component: AppDataComponent) => {
      const containerData = component.data as ContainerData;
      return containerData?.content?.widgets?.some(
        (widget) => widget.widgetGuid === componentId,
      );
    },
  );

  return pageComponent?.data as ContainerData;
};

export const getComponentDataFromCSM = (
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
  appData?: AppData,
): AppUnifiedComponentData | undefined => {
  const compData = documentServicesAPI.components.data.get(compRef);
  if (!appData) {
    if (compData?.appDefinitionId) {
      appData = documentServicesAPI.platform.getAppDataByAppDefId(
        compData.appDefinitionId,
      );
    }
  }

  if (
    appData?.appFields?.newEditorSchemaVersion ===
    APP_SCHEMA_VERSIONS.EXTERNAL_UNIFIED_COMPONENT
  ) {
    return appData.components?.find(
      ({ componentId }) => compData.widgetId === componentId,
    )?.data as AppUnifiedComponentData;
  }

  return undefined;
};

export const getComponentIdentifiers = (
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
  appData?: AppData,
): BaseInfo | undefined =>
  getComponentDataFromCSM(documentServicesAPI, compRef, appData)?.base;

export const isComponentEssential = (
  componentId: string,
  appData: AppData,
  shouldReadDataFromNewSchema: boolean,
  tpaPageId?: string,
) => {
  const pageData = getPageFromAppData(appData, tpaPageId);

  const pageWidgetData = pageData?.content?.widgets?.find(
    (widget) => widget.widgetGuid === componentId,
  );

  if (
    shouldReadDataFromNewSchema &&
    pageWidgetData?.essential !== undefined &&
    pageWidgetData?.essential !== null
  ) {
    return pageWidgetData.essential;
  }

  // DEPRECATED: trying to read essential prop from component definition
  const compDefinition = appData.components?.find(
    (comp) => comp.componentId === componentId,
  )?.data as AppUnifiedComponentData;

  return !!compDefinition?.installation?.base?.essential;
};

export const getTpaPageIdByWidgetCompRef = (
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): string | undefined => {
  const pageRef = documentServicesAPI.components.getPage(compRef);
  const pageData = documentServicesAPI.pages.data.get(pageRef?.id);

  return pageData?.tpaPageId;
};

export const isComponentEssentialByRef = (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  compRef: CompRef,
) => {
  const dsItem = documentServicesAPI.components.data.get(compRef);
  const appDefinitionId = dsItem?.appDefinitionId || dsItem?.applicationId;

  if (!appDefinitionId) {
    return false;
  }

  const appData =
    documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);

  if (!appData) {
    return false;
  }

  const componentId = getComponentId(dsItem);
  const tpaPageId = getTpaPageIdByWidgetCompRef(documentServicesAPI, compRef);

  const shouldReadDataFromNewSchema = experimentsAPI.enabled(
    'specs.editor-platform.unified-components-proto-changes',
  );

  return (
    !!componentId &&
    isComponentEssential(
      componentId,
      appData,
      shouldReadDataFromNewSchema,
      tpaPageId,
    )
  );
};

export const isPageEssential = (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  pageId: string,
): boolean => {
  const { appDefinitionId, tpaPageId } =
    documentServicesAPI.pages.data.get(pageId);

  if (!appDefinitionId) {
    return false;
  }

  const appData =
    documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);

  if (!appData) {
    return false;
  }

  const pageData = getPageFromAppData(appData, tpaPageId);

  const widgets = pageData?.content?.widgets || [];

  const shouldReadDataFromNewSchema = experimentsAPI.enabled(
    'specs.editor-platform.unified-components-proto-changes',
  );

  return widgets.some(
    (widget) =>
      widget.widgetGuid &&
      isComponentEssential(
        widget.widgetGuid,
        appData,
        shouldReadDataFromNewSchema,
        pageData?.base?.id,
      ),
  );
};

const getPageFromAppData = (
  { components: appComponents }: AppData,
  tpaPageId?: string,
): ContainerData | undefined => {
  if (!tpaPageId) {
    return;
  }

  return appComponents?.find(
    (component: AppDataComponent) =>
      (component.data as AppUnifiedComponentData)?.base?.id === tpaPageId,
  )?.data as ContainerData;
};

export const isPageDuplicatable = (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  pageId: string,
): boolean => {
  const { appDefinitionId, tpaPageId } =
    documentServicesAPI.pages.data.get(pageId);

  if (!appDefinitionId) {
    return true;
  }

  const appComponents =
    documentServicesAPI.platform.getAppDataByAppDefId(
      appDefinitionId,
    )?.components;

  if (!appComponents?.length) {
    return true;
  }

  const containerComponent = appComponents.find((component) => {
    const componentData = component?.data;
    return (componentData as AppUnifiedComponentData)?.base?.id === tpaPageId;
  });
  const pageData = containerComponent?.data;

  const isDuplicatable = (pageData as AppUnifiedComponentData)?.editorSettings
    ?.base?.duplicatable;

  if (containerComponent?.type === ComponentType.UNIFIED_LIGHTBOX) {
    return isDuplicatable ?? true;
  }

  const shouldReadDataFromNewSchema = experimentsAPI.enabled(
    'specs.editor-platform.unified-components-proto-changes',
  );

  const deprecatedIsDuplicatable =
    (pageData as AppUnifiedComponentData)?.installation?.base?.maxInstances !==
    1;

  return shouldReadDataFromNewSchema
    ? isDuplicatable ?? deprecatedIsDuplicatable
    : deprecatedIsDuplicatable;
};

export const isWidgetStretchable = (
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
  appData?: AppData,
) => {
  const unifiedWidgetData = getComponentDataFromCSM(
    documentServicesAPI,
    compRef,
    appData,
  ) as UnifiedWidget;

  if (unifiedWidgetData?.size) {
    return !!unifiedWidgetData.size.width?.allowStretch;
  }

  return undefined;
};

export const isWidgetFullWidth = (
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
  appData?: AppData,
) => {
  const unifiedWidgetData = getComponentDataFromCSM(
    documentServicesAPI,
    compRef,
    appData,
  ) as UnifiedWidget;

  if (unifiedWidgetData?.size) {
    return !!unifiedWidgetData.size.width?.stretchByDefault;
  }

  return undefined;
};
